/**
 * Modal spécialisée pour les détails de service - VERSION CORRIGÉE
 * Remplace l'ancien ModalManager + ServicesModalRender
 */

import { BaseModal, ModalConfig } from './BaseModal';

// Import des types et données nécessaires
interface ServiceDetailExtended {
  id: string;
  title: string;
  description: string;
  features: string[];
  featureColor: string;
  iaValues: string[];
  iaTitle?: string;
  icon: string;
}

export interface ServiceModalData {
  serviceId: string;
  serviceIndex?: number;
}

export class ServiceModal extends BaseModal {
  private currentService: ServiceDetailExtended | null = null;
  private currentServiceIndex: number = 0;
  private services: ServiceDetailExtended[] = [];
  private isDataSet: boolean = false; // 🔧 Flag pour indiquer si setData a été appelé

  constructor(config: Partial<ModalConfig> = {}) {
    const fullConfig: ModalConfig = {
      id: 'service-detail-modal',
      maxWidth: '4xl', // Retour à 4xl pour plus d'espace horizontal
      className: 'service-modal overflow-visible', // Forcer overflow visible
      ...config
    };
    super(fullConfig);
    
    // Charger les services immédiatement
    this.loadServices();
  }

  /**
   * Référencer les éléments après création
   */
  protected setupElementReferences(): void {
    if (!this.elements.modal) return;

    this.elements.header = this.elements.modal.querySelector('.modal-header') as HTMLElement;
    this.elements.body = this.elements.modal.querySelector('.modal-body') as HTMLElement;
    
    if (this.config.showCloseButton) {
      this.elements.closeBtn = this.elements.modal.querySelector('.modal-close-btn') as HTMLElement;
    }
  }

  /**
   * Chargement sécurisé des services
   */
  private async loadServices(): Promise<void> {
    try {
      // Import dynamique pour éviter les erreurs de dépendance circulaire
      const { services } = await import('../sections/ServicesConfig');
      this.services = services || [];
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
      // Fallback avec des services de test
      this.services = [
        {
          id: 'accompagnement-ia',
          title: 'Accompagnement IA',
          description: 'Diagnostic, cas d’usage, choix d’outils et déploiement.',
          features: ['Diagnostic IA', 'Ateliers cas d’usage'],
          featureColor: '#ed4c67',
          iaValues: ['ROI mesuré', 'Gouvernance IA'],
          icon: 'M9.813 3.5L8.5 7.25 4.75 8.563 8.5 9.875l1.313 3.75L11.125 9.875 14.875 8.563 11.125 7.25 9.813 3.5zm5.437 6.5l-.75 2.125L12.375 12.875l2.125.75.75 2.125.75-2.125 2.125-.75-2.125-.75-.75-2.125zM6.25 12.5l-.5 1.5-1.5.5 1.5.5.5 1.5.5-1.5 1.5-.5-1.5-.5-.5-1.5z'
        }
      ];
    }
  }

  /**
   * Définit les données du service à afficher
   */
  public async setData(data: ServiceModalData): Promise<void> {
    // S'assurer que les services sont chargés avant de continuer
    if (!this.services.length) {
      await this.loadServices();
    }

    // Retenter si les services ne sont toujours pas là
    if (!this.services.length) {
      console.error('Impossible de charger les services');
      return;
    }

    if (typeof data.serviceIndex === 'number') {
      this.currentServiceIndex = data.serviceIndex;
      this.currentService = this.services[this.currentServiceIndex];
    } else {
      this.currentServiceIndex = this.services.findIndex(s => s.id === data.serviceId);
      if (this.currentServiceIndex === -1) {
        console.error(`Service avec l'ID '${data.serviceId}' non trouvé. Services disponibles:`, this.services.map(s => s.id));
        // Utiliser le premier service comme fallback
        this.currentServiceIndex = 0;
      }
      this.currentService = this.services[this.currentServiceIndex];
    }

    if (!this.currentService) {
      console.error(`Service non trouvé après recherche`);
      return;
    }

    // Marquer que les données ont été définies
    this.isDataSet = true;

    // Mettre à jour les styles spécifiques au service
    this.updateServiceStyling();
  }

  /**
   * Met à jour le style de la modale selon le service
   */
  private updateServiceStyling(): void {
    if (!this.currentService || !this.elements.content) return;

    // Supprimer les anciennes classes de service
    const serviceClasses = [
      'web-service', 'accompagnement-ia-service', 'strategie-service', 'facturation-service',
      'rgpd-service', 'cybersecurite-service', 'donnees-service', 'processus-service'
    ];
    
    serviceClasses.forEach(cls => {
      this.elements.content?.classList.remove(cls);
    });

    // Ajouter la classe spécifique du service actuel
    if (this.currentService.id) {
      this.elements.content.classList.add(`${this.currentService.id}-service`);
    }

    // Appliquer la couleur du service
    if (this.currentService.featureColor) {
      this.elements.content.style.borderColor = `${this.currentService.featureColor}40`;
      this.elements.content.style.boxShadow = `0 25px 50px -12px ${this.currentService.featureColor}20`;
    }
  }

  /**
   * Configuration des événements spécifiques aux services
   */
  protected setupEventListeners(): void {
    super.setupEventListeners();

    // Navigation entre services avec les flèches
    const navigationHandler = (e: KeyboardEvent) => {
      if (!this.isOpen) return;

      switch (e.key) {
        case 'ArrowLeft':
          this.navigateToService('prev');
          e.preventDefault();
          break;
        case 'ArrowRight':
          this.navigateToService('next');
          e.preventDefault();
          break;
      }
    };

    document.addEventListener('keydown', navigationHandler);
    this.cleanup.push(() => {
      document.removeEventListener('keydown', navigationHandler);
    });
  }

  /**
   * Navigation entre les services
   */
  private navigateToService(direction: 'prev' | 'next'): void {
    if (!this.services.length) return;

    let newIndex = this.currentServiceIndex;

    if (direction === 'prev' && this.currentServiceIndex > 0) {
      newIndex = this.currentServiceIndex - 1;
    } else if (direction === 'next' && this.currentServiceIndex < this.services.length - 1) {
      newIndex = this.currentServiceIndex + 1;
    }

    if (newIndex !== this.currentServiceIndex) {
      // Animation de transition
      if (this.elements.body) {
        this.elements.body.style.opacity = '0.7';
        this.elements.body.style.transform = direction === 'next' ? 'translateX(10px)' : 'translateX(-10px)';
      }

      // Mettre à jour les données après un court délai
      setTimeout(() => {
        this.setData({ serviceId: this.services[newIndex].id, serviceIndex: newIndex });
        this.updateContent();
        
        // Restaurer l'animation
        if (this.elements.body) {
          this.elements.body.style.opacity = '1';
          this.elements.body.style.transform = 'translateX(0)';
        }
      }, 150);
    }
  }

  /**
   * Après le rendu - configurer les boutons de navigation
   */
  protected onRender(): void {
    this.setupNavigationButtons();
    this.setupContactButtons();
    this.updatePositionIndicator();
    this.setupSwipeGestures();
  }

  private setupSwipeGestures(): void {
    if (!this.elements.content) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;
    let isSwipeCandidate = false;
    let swipeStartTime = 0;

    const minSwipeDistance = 50; // Distance minimale pour déclencher le swipe
    const maxVerticalTolerance = 100; // Tolérance verticale pour détecter un swipe horizontal
    const maxSwipeTime = 300; // Temps maximum pour considérer le geste comme un swipe

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      swipeStartTime = Date.now();
      isSwipeCandidate = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartX);
      const deltaY = Math.abs(touch.clientY - touchStartY);

      // Déterminer si c'est un swipe horizontal potentiel
      if (deltaX > 10 && deltaX > deltaY * 1.5) {
        isSwipeCandidate = true;
        e.preventDefault(); // Empêcher le scroll pendant le swipe
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.changedTouches.length !== 1 || !isSwipeCandidate) return;

      const touch = e.changedTouches[0];
      touchEndX = touch.clientX;
      touchEndY = touch.clientY;

      const deltaX = touchEndX - touchStartX;
      const deltaY = Math.abs(touchEndY - touchStartY);
      const swipeTime = Date.now() - swipeStartTime;

      // Vérifier si c'est un swipe valide
      if (Math.abs(deltaX) >= minSwipeDistance && 
          deltaY <= maxVerticalTolerance && 
          swipeTime <= maxSwipeTime) {
        
        if (deltaX > 0) {
          // Swipe vers la droite - service précédent
          if (this.currentServiceIndex > 0) {
            this.navigateToService('prev');
          }
        } else {
          // Swipe vers la gauche - service suivant
          if (this.currentServiceIndex < this.services.length - 1) {
            this.navigateToService('next');
          }
        }
      }

      // Reset des variables
      touchStartX = touchStartY = touchEndX = touchEndY = 0;
      isSwipeCandidate = false;
      swipeStartTime = 0;
    };

    // Ajouter les event listeners avec passive: false pour preventDefault
    this.elements.content.addEventListener('touchstart', handleTouchStart, { passive: true });
    this.elements.content.addEventListener('touchmove', handleTouchMove, { passive: false });
    this.elements.content.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    this.cleanup.push(() => {
      this.elements.content?.removeEventListener('touchstart', handleTouchStart);
      this.elements.content?.removeEventListener('touchmove', handleTouchMove);
      this.elements.content?.removeEventListener('touchend', handleTouchEnd);
    });
  }

  /**
   * Configuration des boutons de navigation
   */
  private setupNavigationButtons(): void {
    if (!this.elements.modal) return;

    const prevBtn = this.elements.modal.querySelector('#prev-service-btn') as HTMLElement;
    const nextBtn = this.elements.modal.querySelector('#next-service-btn') as HTMLElement;

    if (prevBtn) {
      const isDisabled = this.currentServiceIndex <= 0;
      
      if (isDisabled) {
        prevBtn.setAttribute('disabled', 'true');
        prevBtn.style.pointerEvents = 'none';
        prevBtn.style.opacity = '0.3';
      } else {
        prevBtn.removeAttribute('disabled');
        prevBtn.style.pointerEvents = 'auto';
        prevBtn.style.opacity = '1';
        
        const handlePrevClick = () => this.navigateToService('prev');
        prevBtn.addEventListener('click', handlePrevClick);
        
        this.cleanup.push(() => {
          prevBtn.removeEventListener('click', handlePrevClick);
        });
      }
      
      // Appliquer la couleur du service
      if (this.currentService?.featureColor) {
        this.applyServiceColorToButton(prevBtn, this.currentService.featureColor);
      }
    }

    if (nextBtn) {
      const isDisabled = this.currentServiceIndex >= this.services.length - 1;
      
      if (isDisabled) {
        nextBtn.setAttribute('disabled', 'true');
        nextBtn.style.pointerEvents = 'none';
        nextBtn.style.opacity = '0.3';
      } else {
        nextBtn.removeAttribute('disabled');
        nextBtn.style.pointerEvents = 'auto';
        nextBtn.style.opacity = '1';
        
        const handleNextClick = () => this.navigateToService('next');
        nextBtn.addEventListener('click', handleNextClick);
        
        this.cleanup.push(() => {
          nextBtn.removeEventListener('click', handleNextClick);
        });
      }
      
      // Appliquer la couleur du service
      if (this.currentService?.featureColor) {
        this.applyServiceColorToButton(nextBtn, this.currentService.featureColor);
      }
    }
  }

  /**
   * Applique la couleur du service à un bouton
   */
  private applyServiceColorToButton(button: HTMLElement, color: string): void {
    // Utiliser des variables CSS pour la couleur
    button.style.setProperty('--service-color', color);
    button.classList.add('service-colored-btn');
  }

  /**
   * Configuration des boutons de contact (maintenant intégré dans le contenu)
   */
  private setupContactButtons(): void {
    if (!this.elements.modal) return;

    const contactButton = this.elements.modal.querySelector('#modal-contact-btn') as HTMLElement;
    if (contactButton) {
      const handleContactClick = () => {
        this.close();
        setTimeout(() => {
          // Essayer de scroller vers la section contact
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          } else {
            // Si pas de section contact, aller vers la page contact
            window.location.href = 'contact.html';
          }
        }, 300);
      };

      contactButton.addEventListener('click', handleContactClick);
      
      this.cleanup.push(() => {
        contactButton.removeEventListener('click', handleContactClick);
      });
    }
  }

  /**
   * Met à jour l'indicateur de position
   */
  private updatePositionIndicator(): void {
    if (!this.elements.modal) return;

    const currentIndexEl = this.elements.modal.querySelector('#current-service-index');
    const totalServicesEl = this.elements.modal.querySelector('#total-services');
    const positionEl = this.elements.modal.querySelector('#service-position');

    if (currentIndexEl) {
      currentIndexEl.textContent = (this.currentServiceIndex + 1).toString();
    }
    
    if (totalServicesEl) {
      totalServicesEl.textContent = this.services.length.toString();
    }
    
    // Animation de l'indicateur
    if (positionEl) {
      positionEl.classList.add('updating');
      setTimeout(() => {
        positionEl.classList.remove('updating');
      }, 300);
    }
  }

  /**
   * Rendu du contenu du service
   */
  protected renderContent(): string {
    // Si les données n'ont pas encore été définies, afficher un loader
    if (!this.isDataSet) {
      return `
        <div class="flex items-center justify-center h-64">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p class="text-white">Chargement du service...</p>
          </div>
        </div>
      `;
    }

    if (!this.currentService) {
      return '<p class="text-white">Service non trouvé</p>';
    }

    const { title, description, features, featureColor, iaValues, iaTitle = 'Valeur ajoutée IA', icon } = this.currentService;

    // Import sécurisé des couleurs
    const colors = { pink: '#ed4c67', blue: '#3498db' };

    return `
      <div class="service-modal-content">
        <!-- En-tête avec dégradé optimisé -->
        <div class="flex flex-col md:flex-row items-start md:items-center mb-3 pb-3 border-b border-white/20">
          <div class="flex-shrink-0 mb-2 md:mb-0 md:mr-4">
            <div class="relative">
              <!-- Halo lumineux derrière l'icône -->
              <div class="absolute inset-0 rounded-xl filter blur-xl" style="background-color: ${featureColor}30;"></div>

              <div class="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br relative z-10 border shadow-lg" style="background: linear-gradient(135deg, ${featureColor}30, transparent); border-color: ${featureColor}40; box-shadow: 0 10px 25px ${featureColor}10;">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" shape-rendering="geometricPrecision">
                  ${ (icon || '').trim().startsWith('<')
                    ? icon
                    : `<path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" vector-effect=\"non-scaling-stroke\" d=\"${icon}\" />` }
                </svg>
              </div>
            </div>
          </div>
          <div>
            <h3 class="text-xl md:text-2xl font-bold mb-1 text-white">${title}</h3>
            <p class="text-white/90 text-sm md:text-base leading-snug">${description}</p>
          </div>
        </div>

        <!-- Corps du modal en grille optimisé - CONTENU COMPLET -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <!-- Caractéristiques optimisées - TOUTES AFFICHÉES -->
          <div>
            <h4 class="text-base font-bold mb-2 flex items-center text-white">
              <span class="w-5 h-5 rounded-full flex items-center justify-center mr-2 text-white border" style="background-color: ${featureColor}30; border-color: ${featureColor}40;">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Caractéristiques
            </h4>

            <div class="space-y-1.5">
              ${features.map((feature, index) => `
                <div class="flex items-start p-1.5 rounded-md bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/15 transition-all duration-300" style="border-color: ${featureColor}30;">
                  <span class="w-3.5 h-3.5 rounded-full flex items-center justify-center text-white border mr-1.5 mt-0.5 flex-shrink-0" style="background-color: ${featureColor}20; border-color: ${featureColor}30;">
                    <span class="text-xs font-bold">${index + 1}</span>
                  </span>
                  <span class="text-white/95 text-xs leading-tight">${feature}</span>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Valeur ajoutée IA optimisée - TOUTES AFFICHÉES -->
          <div>
            <h4 class="text-base font-bold mb-2 flex items-center text-white">
              <span class="w-5 h-5 rounded-full flex items-center justify-center mr-2 text-white border" style="background: linear-gradient(to right, ${colors.pink}30, ${colors.blue}30); border-color: ${colors.pink}40;">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
              ${iaTitle}
            </h4>

            <div class="space-y-1.5">
              ${iaValues.map(value => `
                <div class="p-1.5 rounded-md border border-white/10 transition-all duration-300" style="background: linear-gradient(to right, ${featureColor}20, ${featureColor}05); border-color: ${featureColor}30;">
                  <span class="text-white/95 text-xs leading-tight block">${value}</span>
                </div>
              `).join('')}
            </div>
          </div>
        </div>

        <!-- CTA "Parlons de votre projet" intégré -->
        <div class="text-center mt-6 pt-4 border-t border-white/10">
          <button id="modal-contact-btn" class="inline-flex items-center gap-2 px-6 py-3 font-semibold text-white rounded-lg transition-all duration-300 hover:shadow-lg transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-pink-200" style="background: linear-gradient(135deg, #ed4c67, #e84393);">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            Parlons de votre projet
          </button>
        </div>

      </div>
      
      <style>
        .service-colored-btn:hover {
          background-color: var(--service-color, #3498db) !important;
          opacity: 0.2;
        }
        
        @keyframes shine {
          0% {
            transform: translateX(-130%);
            opacity: 0;
          }
          30% {
            opacity: 0.7;
          }
          50% {
            transform: translateX(0%);
            opacity: 0.9;
          }
          80% {
            opacity: 0.7;
          }
          100% {
            transform: translateX(130%);
            opacity: 0;
          }
        }
        
        .animate-shine {
          animation: shine 1.8s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        /* Ajustements d'alignement et d'espacement */
        .service-modal-content {
          padding-bottom: 1.5rem;
        }

        .service-modal-content .space-y-1\.5 > div {
          margin-bottom: 0.35rem;
        }

        .service-modal-content .grid {
          gap: 0.75rem;
        }

        @media (max-width: 1024px) {
          .service-modal-content {
            padding-bottom: 1.25rem;
          }
          
          .service-modal-content .text-base {
            font-size: 0.875rem;
          }
        }

        @media (max-width: 768px) {
          .service-modal-content {
            padding-bottom: 1.25rem;
          }
          
          .service-modal-content .grid {
            grid-template-columns: 1fr;
            gap: 0.6rem;
          }
          
          .service-modal-content h3 {
            font-size: 1.125rem !important;
          }
          
          .service-modal-content .text-base {
            font-size: 0.85rem !important;
          }
          
          .service-modal-content .space-y-1\.5 > div {
            padding: 0.35rem 0.5rem;
          }
          
          .contact-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 600px) {
          .service-modal-content {
            padding-bottom: 1rem;
          }
          
          .service-modal-content .space-y-1\.5 > div {
            margin-bottom: 0.2rem;
          }
          
          .service-modal-content .grid {
            gap: 0.45rem;
          }
        }

        /* Styles pour les boutons de navigation améliorés */
        #prev-service-btn:disabled,
        #next-service-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          transform: none !important;
        }
        
        #prev-service-btn:disabled:hover,
        #next-service-btn:disabled:hover {
          background-color: rgba(255,255,255,0.2) !important;
          border-color: rgba(255,255,255,0.4) !important;
          transform: none !important;
          scale: 1 !important;
        }
        
        /* SUPPRESSION SCROLLBARS NAVIGATEUR */
        body:has(.service-modal.show) {
          overflow: hidden;
        }
        
        .service-modal::-webkit-scrollbar {
          display: none;
        }
        
        .service-modal {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      </style>
    `;
  }
}
